// Watchlist (Phase 5 Step 10 — largest mid-layer extraction, dependency-injected).
// The 732-line IIFE moved verbatim (8-space indent kept). Closure state fully encapsulated (nothing
// outside reaches it). Imports only the leaf ui; its outbound reaches (3 renderers on the async
// rollback path, projectRvol/confirmationState + 2 mutable-let reads via CONFIRM.score) are INJECTED
// via configure() at boot -> breaks the bidirectional (non-re-entrant) render cycle, no import cycle.
import { ui } from './ui.js';

let _deps = {};
export function configure(d) { _deps = { ..._deps, ...d }; }

        export const Watchlist = (() => {
            const KEY_ITEMS = 'abWatchlistV1';      // legacy single-list store — kept for one-time migration only
            const KEY_LOG   = 'abAlertLogV1';
            const KEY_PREFS = 'abWatchlistPrefsV1';
            const KEY_ACTIVE = 'abActiveWatchlistV1'; // remembers which list tab was active (per browser)
            const LOG_CAP   = 500;

            // ── Storage adapter (the only place that touches localStorage) ────────────
            const store = {
                read(key, fallback) {
                    try {
                        const raw = localStorage.getItem(key);
                        if (!raw) return fallback;
                        const parsed = JSON.parse(raw);
                        return parsed ?? fallback;
                    } catch (e) { return fallback; }
                },
                write(key, value) {
                    try { localStorage.setItem(key, JSON.stringify(value)); return true; }
                    catch (e) { console.warn('Watchlist storage write failed:', e); return false; }
                }
            };

            // ── In-memory state ───────────────────────────────────────────────────────
            // Membership now lives server-side (per account). `lists` is hydrated from the
            // server via load(); `items` is a live reference to the ACTIVE list's items
            // array so the synchronous readers (all/has/get/count) and sync()/render stay
            // unchanged. Mutations operate on `items` in place (push/splice) so the
            // reference never goes stale, and fire optimistic background server calls.
            let lists = [];           // [{ id, name, position, items:[...] }]
            let activeListId = null;
            let items = [];           // === activeList().items (same array object)
            let loaded = false;
            let prefs = { sortKey: 'addedAt', sortDir: 'desc', logFilter: { ticker: null, type: null, unreadOnly: false } };

            // MARKET-WIDE ALERT LOG (independent of watchlist). Every enriched scanned
            // stock can append here on a signal transition. Persisted as a running journal.
            //   alertLog    : [{ id, ticker, name, type, status, confScore, grade, gatePass,
            //                     proximity, price, detail, firedAt, read }]  newest-last
            //   signalState : { TICKER: { hasAlpha, hasBeta, omega, deltaTrigger, forming } }
            //                 last-seen state per ticker, so transitions can be detected
            //                 market-wide without storing every ticker in the watchlist.
            let alertLog = [];
            let signalState = {};

            // Membership is server-backed now, so persist() is a no-op — sync() still calls
            // it every scan to flush snapshot updates, and we deliberately DON'T write those
            // to the server (they're recomputed live from screenerAllResults on render).
            // Mutations (add/remove/update) persist to the server explicitly + optimistically.
            function persist() { /* server-backed: nothing to write locally */ }
            function persistPrefs() { store.write(KEY_PREFS, { v: 1, prefs }); }
            function persistLog() { store.write(KEY_LOG, { v: 2, savedAt: Date.now(), alertLog, signalState }); }

            // ── Server I/O + active-list plumbing ─────────────────────────────────────
            async function api(method, path, body) {
                const res = await fetch(path, {
                    method,
                    headers: body ? { 'Content-Type': 'application/json' } : {},
                    credentials: 'same-origin',
                    body: body ? JSON.stringify(body) : undefined
                });
                if (!res.ok) {
                    let b = null; try { b = await res.json(); } catch (_) {}
                    const e = new Error((b && b.error) || ('http ' + res.status));
                    e.status = res.status; e.code = b && b.code; throw e;
                }
                return res.status === 204 ? null : res.json().catch(() => null);
            }
            function _toast(msg) { try { if (typeof ui !== 'undefined' && ui.status) ui.status.textContent = msg; } catch (e) {} console.warn('[watchlist]', msg); }
            function activeList() { return lists.find(l => l.id === activeListId) || null; }
            function repoint() { const a = activeList(); items = a ? a.items : []; }
            function normItem(it) {
                return { ticker: it.ticker, name: it.name || it.ticker, addedAt: it.addedAt || Date.now(),
                         snapshot: it.snapshot || null, alerts: [], note: it.note || null,
                         flag: it.flag || null, tags: Array.isArray(it.tags) ? it.tags : [],
                         position: Number.isFinite(it.position) ? it.position : 0, section: it.section || null };
            }
            // Hydrate all of the signed-in user's lists from the server.
            async function load() {
                const data = await api('GET', '/api/watchlists');
                lists = ((data && data.lists) || []).map(l => ({ id: l.id, name: l.name, position: l.position, sections: Array.isArray(l.sections) ? l.sections : [], items: (l.items || []).map(normItem) }));
                let stored = Number(localStorage.getItem(KEY_ACTIVE));
                activeListId = lists.some(l => l.id === stored) ? stored : (lists[0] ? lists[0].id : null);
                repoint();
                loaded = true;
                return lists;
            }
            function isLoaded() { return loaded; }
            function reset() { lists = []; activeListId = null; items = []; loaded = false; }   // on logout
            function getLists() { return lists.map(l => ({ id: l.id, name: l.name, position: l.position, count: l.items.length })); }
            function getActiveId() { return activeListId; }
            function setActive(id) {
                if (!lists.some(l => l.id === id)) return false;
                activeListId = id; repoint();
                try { localStorage.setItem(KEY_ACTIVE, String(id)); } catch (e) {}
                return true;
            }
            // Create a list. On a tier-cap rejection (403) surface the server message and
            // return null so callers can no-op cleanly.
            async function createList(name) {
                let d;
                try {
                    d = await api('POST', '/api/watchlists', { name: String(name || '').trim() || 'New list' });
                } catch (e) {
                    if (e.status === 403) { try { alert(e.message || 'Watchlist limit reached.'); } catch (_) {} return null; }
                    throw e;
                }
                if (d && d.list) { lists.push({ id: d.list.id, name: d.list.name, position: d.list.position, sections: [], items: [] }); setActive(d.list.id); }
                return d && d.list;
            }
            async function renameList(id, name) {
                await api('PATCH', '/api/watchlists/' + id, { name: String(name || '').trim() });
                const l = lists.find(x => x.id === id); if (l) l.name = String(name || '').trim();
            }
            async function deleteList(id) {
                const d = await api('DELETE', '/api/watchlists/' + id);
                lists = lists.filter(l => l.id !== id);
                if (d && d.defaultList) lists.push({ id: d.defaultList.id, name: d.defaultList.name, position: d.defaultList.position, sections: [], items: [] });
                if (activeListId === id) setActive(lists[0] ? lists[0].id : null);
                repoint();
            }
            async function reorderLists(orderedIds) {
                await api('POST', '/api/watchlists/reorder', { order: orderedIds });
                orderedIds.forEach((id, i) => { const l = lists.find(x => x.id === id); if (l) l.position = i; });
                lists.sort((a, b) => a.position - b.position);
            }

            // ── Sections + manual item ordering (drag-and-drop) ───────────────────────
            function getSections() { const l = activeList(); return l ? (l.sections || []).slice() : []; }
            // Persist the active list's ordered section names to the server (optimistic).
            async function _putSections(sections) {
                const l = activeList(); if (!l) return;
                l.sections = sections.slice();
                if (activeListId) api('PUT', '/api/watchlists/' + activeListId + '/sections', { sections }).catch(() => _toast('Could not save sections'));
            }
            // Persist the active list's full item order + section assignment (optimistic). The
            // in-memory items array is reordered to match so render and server agree.
            async function reorderItems(order) {
                const l = activeList(); if (!l) return;
                const bySym = new Map(l.items.map(it => [it.ticker, it]));
                const next = [];
                order.forEach((o, i) => { const it = bySym.get(o.ticker); if (it) { it.position = i; it.section = o.section || null; next.push(it); bySym.delete(o.ticker); } });
                bySym.forEach(it => next.push(it));   // keep any items not named in `order`
                l.items.length = 0; Array.prototype.push.apply(l.items, next);
                items = l.items;
                if (activeListId) api('POST', '/api/watchlists/' + activeListId + '/items/reorder', { order: l.items.map(it => ({ ticker: it.ticker, section: it.section || null })) }).catch(() => _toast('Could not save order'));
            }
            async function addSection(name) {
                const nm = String(name || '').trim(); if (!nm) return;
                const secs = getSections(); if (secs.includes(nm)) return;
                secs.push(nm); await _putSections(secs);
            }
            async function renameSection(from, to) {
                const nm = String(to || '').trim(); if (!nm || nm === from) return;
                const l = activeList(); if (!l) return;
                const secs = getSections().map(s => s === from ? nm : s);
                await _putSections(secs);
                l.items.forEach(it => { if (it.section === from) it.section = nm; });
                await reorderItems(l.items.map(it => ({ ticker: it.ticker, section: it.section })));
            }
            async function deleteSection(name) {
                const l = activeList(); if (!l) return;
                await _putSections(getSections().filter(s => s !== name));
                l.items.forEach(it => { if (it.section === name) it.section = null; });
                await reorderItems(l.items.map(it => ({ ticker: it.ticker, section: it.section })));
            }
            async function reorderSections(orderedNames) { await _putSections(orderedNames.slice()); }

            function hydrate() {
                const pwrap = store.read(KEY_PREFS, null);
                if (pwrap && pwrap.prefs) prefs = { ...prefs, ...pwrap.prefs };
                const lwrap = store.read(KEY_LOG, null);
                if (lwrap) {
                    alertLog = Array.isArray(lwrap.alertLog) ? lwrap.alertLog : [];
                    signalState = (lwrap.signalState && typeof lwrap.signalState === 'object') ? lwrap.signalState : {};
                }
            }

            // ── Snapshot builder: capture the engine values for a row at add/sync time ─
            function snapshotFromRow(row) {
                if (!row) return null;
                return {
                    score: Number.isFinite(row.score) ? row.score : null,
                    rating: row._rating != null ? row._rating : null,
                    stage: row.stage ?? null,
                    stageLabel: row.stageLabel ?? null,
                    signal: row.signal ?? 'None',
                    hasAlpha: !!row.hasAlpha, hasBeta: !!row.hasBeta,
                    alphaDays: Number.isFinite(row.alphaDays) ? row.alphaDays : null,
                    betaDays: Number.isFinite(row.betaDays) ? row.betaDays : null,
                    beta: row.beta || null, alpha: row.alpha || null,   // server-computed ratings (lib/signals.js)
                    deltaTrigger: !!row.deltaTrigger,
                    deltaDays: Number.isFinite(row.deltaDays) ? row.deltaDays : null,
                    omegaSignal: row.omegaSignal ?? 'NONE',
                    omegaLabel: row.omegaLabel ?? null,
                    rmv: Number.isFinite(row.rmv) ? row.rmv : null,
                    rvol: Number.isFinite(row.rvol) ? row.rvol : null,
                    stage2: row.stage === 2 || row.stage2 === true || /stage\s*2/i.test(row.stageLabel || ''),
                    forming: row.forming || null,
                    rsi1d: Number.isFinite(row.rsi1d) ? row.rsi1d : null,
                    rsiExpansion: Number.isFinite(row.rsiExpansion) ? row.rsiExpansion : null,
                    rsPower: Number.isFinite(row.rsPower) ? row.rsPower : null,
                    rsPowerTier: row.rsPowerTier ?? null,
                    avwapDefense: Number.isFinite(row.avwapDefense) ? row.avwapDefense : null,
                    avwapDistAtr: Number.isFinite(row.avwapDistAtr) ? row.avwapDistAtr : null,
                    dollarVolume: Number.isFinite(row.dollarVolume) ? row.dollarVolume : null,
                    adrPct: Number.isFinite(row.adrPct) ? row.adrPct : null,
                    price: Number.isFinite(row.price) ? row.price : null,
                    pct3m: Number.isFinite(row.pct3m) ? row.pct3m : null,
                    tier: row.tier ?? null,
                    udVolRatio: Number.isFinite(row.upDownVolRatio) ? row.upDownVolRatio : null,
                    accumulationDays: Number.isFinite(row.accumulationDays) ? row.accumulationDays : null,
                    distributionDays: Number.isFinite(row.distributionDays) ? row.distributionDays : null,
                    belowHighPct: Number.isFinite(row.belowHighPct) ? row.belowHighPct : null,
                    rsRating: Number.isFinite(row.rsRating) ? row.rsRating : null,
                    industryGroup: row.industryGroup ?? null,
                    sector: row.sector ?? null,
                    bqs: Number.isFinite(row.bqsGrade) ? row.bqsGrade : null,
                    bqsMode: row.bqsMode ?? null,
                    at: Date.now()
                };
            }

            // ── Core API ──────────────────────────────────────────────────────────────
            function has(ticker) {
                if (!ticker) return false;
                const t = ticker.toUpperCase();
                return items.some(i => i.ticker === t);
            }
            function get(ticker) {
                const t = (ticker || '').toUpperCase();
                return items.find(i => i.ticker === t) || null;
            }
            // Membership across ALL of the user's lists (the screener star reflects this).
            function hasAny(ticker) {
                if (!ticker) return false;
                const t = ticker.toUpperCase();
                return lists.some(l => l.items.some(i => i.ticker === t));
            }
            function listsContaining(ticker) {
                const t = (ticker || '').toUpperCase();
                return lists.filter(l => l.items.some(i => i.ticker === t)).map(l => l.id);
            }
            function _repaintWatched() {
                if (typeof _deps.renderWatchlist === 'function') _deps.renderWatchlist();
                if (typeof _deps.updateWatchlistTabBadge === 'function') _deps.updateWatchlistTabBadge();
                if (typeof _deps.renderScreenerResults === 'function') _deps.renderScreenerResults();
            }
            // Optimistic add to a SPECIFIC list: push locally, POST in the background, roll
            // back on failure. Synchronous boolean. Unknown list / already present → false.
            function addTo(listId, ticker, name, row) {
                if (!ticker) return false;
                const list = lists.find(l => l.id === listId);
                if (!list) return false;
                const t = ticker.toUpperCase();
                if (list.items.some(i => i.ticker === t)) return false;
                const snap = snapshotFromRow(row);
                const item = { ticker: t, name: name || (row && row.name) || t, addedAt: Date.now(), snapshot: snap, alerts: [], note: null, flag: null, tags: [] };
                list.items.push(item);
                api('POST', '/api/watchlists/' + listId + '/items', { ticker: t, name: item.name, snapshot: snap })
                    .catch(() => { const i = list.items.indexOf(item); if (i >= 0) list.items.splice(i, 1); _toast('Could not add ' + t); _repaintWatched(); });
                return true;
            }
            function removeFrom(listId, ticker) {
                const list = lists.find(l => l.id === listId);
                if (!list) return false;
                const t = (ticker || '').toUpperCase();
                const idx = list.items.findIndex(i => i.ticker === t);
                if (idx < 0) return false;
                const [removed] = list.items.splice(idx, 1);
                api('DELETE', '/api/watchlists/' + listId + '/items/' + encodeURIComponent(t))
                    .catch(() => { list.items.splice(idx, 0, removed); _toast('Could not remove ' + t); _repaintWatched(); });
                return true;
            }
            // add/remove/toggle operate on the ACTIVE list (preserves existing callers).
            function add(ticker, name, row) { return activeList() ? addTo(activeListId, ticker, name, row) : false; }
            function remove(ticker) { return removeFrom(activeListId, ticker); }
            function toggle(ticker, name, row) {
                if (has(ticker)) { remove(ticker); return false; }
                add(ticker, name, row); return true;
            }
            // Patch fields on an item locally; only note/flag/tags persist to the server
            // (transient keys like the sparkline cache stay client-only — see spark update).
            function update(ticker, patch) {
                const t = (ticker || '').toUpperCase();
                const it = items.find(i => i.ticker === t);
                if (!it) return false;
                Object.assign(it, patch || {});
                const sp = {};
                if ('note' in patch) sp.note = patch.note;
                if ('flag' in patch) sp.flag = patch.flag;
                if ('tags' in patch) sp.tags = patch.tags;
                if (Object.keys(sp).length && activeListId) api('PATCH', '/api/watchlists/' + activeListId + '/items/' + encodeURIComponent(t), sp).catch(() => {});
                return true;
            }
            // Bulk add (import). Optimistic local push + one bulk POST. Returns count added.
            function addMany(tickers) {
                if (!activeList()) return 0;
                const arr = items, added = [];
                (tickers || []).forEach(t => {
                    const u = String(t || '').toUpperCase();
                    if (u && !arr.some(i => i.ticker === u)) { const it = { ticker: u, name: u, addedAt: Date.now(), snapshot: null, alerts: [], note: null, flag: null, tags: [] }; arr.push(it); added.push(it); }
                });
                if (added.length) api('POST', '/api/watchlists/' + activeListId + '/items/bulk', { items: added.map(i => ({ ticker: i.ticker, name: i.name })) })
                    .catch(() => { added.forEach(it => { const i = arr.indexOf(it); if (i >= 0) arr.splice(i, 1); }); if (typeof _deps.renderWatchlist === 'function') _deps.renderWatchlist(); });
                return added.length;
            }
            // Bulk remove (multi-select). Optimistic in-place splice + background deletes.
            function removeMany(tickers) {
                const set = new Set((tickers || []).map(x => String(x || '').toUpperCase()));
                const arr = items, removed = [], listId = activeListId;
                for (let i = arr.length - 1; i >= 0; i--) { if (set.has(arr[i].ticker)) { removed.push(arr[i]); arr.splice(i, 1); } }
                removed.forEach(it => api('DELETE', '/api/watchlists/' + listId + '/items/' + encodeURIComponent(it.ticker)).catch(() => {}));
                return removed.length;
            }
            function all() { return items.slice(); }
            function count() { return items.length; }

            // ── Alert helpers ─────────────────────────────────────────────────────────
            // Canonical signal types: alpha, beta, omega, delta. A "fired" alert is created
            // only on a transition (signal absent -> present) and deduped by a stable id.
            const ALERT_CAP = 60; // keep last N alerts per ticker; trims oldest

            // Map raw engine omega codes to canonical alert types.
            const OMEGA_TYPE = { OMEGA20: 'oprime', OMEGA55: 'onova', OMEGA_EXIT: 'otitan' };
            function omegaTypeOf(s) { return s && OMEGA_TYPE[s.omegaSignal] || null; }

            // Build the stable dedupe id: ticker + type + the signal's own date key.
            // Same signal on the same day = same id = never re-fires.
            function alertId(ticker, type, dayKey) {
                return `${ticker}-${type}-${dayKey}`;
            }

            // Compose the alert detail line. Leads with the UNIQUE info (projected RVOL +
            // gate status) since price lives in its own column and grade/provisional show in
            // the Conf cell — so the truncated detail cell spends its width on what matters.
            function alertDetail(type, snap, conf) {
                const daysFor = type === 'alpha' ? snap.alphaDays
                    : type === 'beta' ? snap.betaDays : 0;
                const ago = (daysFor == null) ? 'today'
                    : daysFor <= 0 ? 'today' : `${daysFor}d`;
                const provisional = conf && conf.state === 'provisional';
                const proj = conf && Number.isFinite(conf.projRvol) ? conf.projRvol : null;
                const raw = conf && Number.isFinite(conf.rawRvol) ? conf.rawRvol
                    : (Number.isFinite(snap.rvol) ? snap.rvol : null);
                // Transparent RVOL: intraday show BOTH the projected full-day estimate and the
                // raw-so-far ("~2.3× proj · 0.6× raw") so the projection is explicit. Off-hours
                // proj == raw, so collapse to a single number.
                let rvolTxt;
                if (provisional && Number.isFinite(proj) && Number.isFinite(raw) && Math.abs(proj - raw) > 0.05) {
                    rvolTxt = `~${proj.toFixed(1)}× proj · ${raw.toFixed(1)}× raw`;
                } else if (Number.isFinite(proj)) {
                    rvolTxt = `${proj.toFixed(1)}× vol`;
                } else if (Number.isFinite(raw)) {
                    rvolTxt = `${raw.toFixed(1)}× vol`;
                } else rvolTxt = 'vol n/a';
                const gateTxt = conf && conf.gatePass ? 'RVOL+S2 ✓' : 'gate ✗';
                return `${rvolTxt} · ${gateTxt} · ${ago}`;
            }

            // Detail line for a FORMING (pre-trigger) alert: how close to firing.
            function formingDetail(snap) {
                const f = snap.forming;
                const px = snap.price != null ? `$${snap.price.toFixed(2)}` : '--';
                if (!f) return `approaching · ${px}`;
                return `${f.note} · ${px} · ${f.proximity}% there`;
            }

            // The "day key" each signal type uses for dedupe.
            function dayKeyFor(type, snap) {
                const today = new Date(snap.at).toISOString().slice(0, 10);
                if (type === 'alpha') return `a${snap.alphaDays ?? 'x'}`;
                if (type === 'beta')  return `b${snap.betaDays ?? 'x'}`;
                // Omega variants + Delta have no engine day-offset, so key on the appearance
                // date. A signal that goes dormant then returns on a later date fires fresh.
                if (type === 'oprime' || type === 'onova' || type === 'otitan') return `${type}-${today}`;
                if (type === 'delta') return `d${today}`;
                return 'x';
            }

            // Given prev/next snapshots, return the list of newly-fired alert types.
            function firedTransitions(prev, next) {
                const fired = [];
                if (next.hasAlpha && !prev.hasAlpha) fired.push('alpha');
                if (next.hasBeta && !prev.hasBeta) fired.push('beta');
                // Each Omega variant fires on its own NONE/other -> this-variant transition.
                const prevOm = omegaTypeOf(prev), nextOm = omegaTypeOf(next);
                if (nextOm && nextOm !== prevOm) fired.push(nextOm);
                if (next.deltaTrigger && !prev.deltaTrigger) fired.push('delta');
                return fired;
            }

            // ── CONFIRMATION SCORE (0–100) ──────────────────────────────────────────
            // How much to trust that THIS alert is a real, in-context leadership move now.
            // Three layers (shown in the alert's hover breakdown):
            //   ① TRIGGER (40)  is the signal real?  projected RVOL · RSI expansion ·
            //                    freshness · RMV contraction · demand (U/D vol, accum/dist)
            //   ② SETUP (35)    is the stock worth it?  conviction /1000 · RS leadership ·
            //                    Delta (leading accumulation) · Stage 2 · room below high
            //   ③ CONTEXT (±5)  does the environment help?  sector rotation (in/out) +
            //                    market tape (Risk-On vs Caution) — a MILD nudge only.
            // HARD GATE (unchanged): RVOL ≥ 1.5 AND Stage 2 — fail either and it can't be
            // graded A/B (context can never lift a gate-fail). Grade A/B requires the gate.
            const CONFIRM = {
                RVOL_GATE: 1.5,
                score(type, snap) {
                    // Project partial-session RVOL to a full-day estimate (no-op off-hours).
                    const rawRvol = snap.rvol;
                    const rvol = (typeof _deps.projectRvol === 'function') ? _deps.projectRvol(rawRvol) : rawRvol;
                    const rmv = snap.rmv, convScore = snap.score;
                    const stage2 = !!snap.stage2;
                    const days = type === 'alpha' ? snap.alphaDays : type === 'beta' ? snap.betaDays : 0;
                    const fresh = (days == null) ? 0.5 : days <= 0 ? 1 : days <= 2 ? 0.7 : days <= 5 ? 0.4 : 0.1;

                    // ── ① TRIGGER (40) ──────────────────────────────────────────────
                    let vol = 0;
                    if (Number.isFinite(rvol)) vol = rvol >= 2.5 ? 16 : rvol >= 1.5 ? 13 : rvol >= 1.1 ? 8 : rvol >= 0.8 ? 4 : 1;
                    let rsiPts = 0;
                    const rsiExp = snap.rsiExpansion, rsiLvl = snap.rsi1d;
                    if (Number.isFinite(rsiExp)) {
                        rsiPts = rsiExp >= 12 ? 9 : rsiExp >= 6 ? 7 : rsiExp >= 2 ? 4.5 : rsiExp >= -2 ? 2 : 0;
                        if (Number.isFinite(rsiLvl) && rsiLvl >= 80) rsiPts = Math.min(rsiPts, 4.5); // overbought cap
                    }
                    const freshPts = fresh * 7;
                    let coil = 0;
                    if (Number.isFinite(rmv)) coil = rmv <= 25 ? 5 : rmv <= 45 ? 3.5 : rmv <= 65 ? 2 : 0.5;
                    let demand = 1.5;
                    const ud = snap.udVolRatio, acc = snap.accumulationDays, dist = snap.distributionDays;
                    if (Number.isFinite(ud)) demand = ud >= 1.5 ? 3 : ud >= 1.1 ? 2.2 : ud >= 0.9 ? 1.5 : 0.5;
                    else if (Number.isFinite(acc) && Number.isFinite(dist)) demand = acc > dist ? 2.5 : acc === dist ? 1.5 : 0.5;
                    const trigger = Math.min(40, vol + rsiPts + freshPts + coil + demand);

                    // ── ② SETUP (35) ────────────────────────────────────────────────
                    let conv = 1;
                    if (Number.isFinite(convScore)) conv = convScore >= 800 ? 15 : convScore >= 650 ? 12 : convScore >= 500 ? 8 : convScore >= 350 ? 4 : 1;
                    const tierName = snap.rsPowerTier || '';
                    let rsLead = 1;
                    if (/Delta|RS NH/i.test(tierName)) rsLead = 8;
                    else if (/Primed/i.test(tierName)) rsLead = 6;
                    else if (/Building/i.test(tierName)) rsLead = 4;
                    else if (/Firm/i.test(tierName)) rsLead = 2;
                    else if (Number.isFinite(snap.rsRating)) rsLead = snap.rsRating >= 90 ? 8 : snap.rsRating >= 80 ? 6 : snap.rsRating >= 60 ? 4 : snap.rsRating >= 40 ? 2 : 1;
                    let deltaPts = 0;
                    if (snap.deltaTrigger) deltaPts = (Number.isFinite(snap.deltaDays) && snap.deltaDays <= 3) ? 6 : 4;
                    const stagePts = stage2 ? 3 : 0;
                    let roomPts = 1.5;
                    if (Number.isFinite(snap.belowHighPct)) roomPts = snap.belowHighPct <= 15 ? 3 : snap.belowHighPct <= 25 ? 2 : 1;
                    const setup = Math.min(35, conv + rsLead + deltaPts + stagePts + roomPts);

                    // base = trigger + setup, scaled to 0..100
                    const base = Math.round((trigger + setup) / 75 * 100);

                    // ── ③ CONTEXT (mild nudge ±5) ───────────────────────────────────
                    let nudge = 0; const ctxBits = [];
                    const grp = (typeof _deps.getGroupRankingData() !== 'undefined' && Array.isArray(_deps.getGroupRankingData()) && (snap.industryGroup || snap.sector))
                        ? (_deps.getGroupRankingData().find(g => g.name === snap.industryGroup) || _deps.getGroupRankingData().find(g => g.name === snap.sector))
                        : null;
                    if (grp && grp.rotationTag) {
                        if (grp.rotationTag === 'in' || grp.rotationTag === 'leader') { nudge += 2.5; ctxBits.push('group ' + grp.rotationTag); }
                        else if (grp.rotationTag === 'out') { nudge -= 2.5; ctxBits.push('group out'); }
                    }
                    const tape = (typeof _deps.getLastMarketTape() !== 'undefined' && _deps.getLastMarketTape()) ? _deps.getLastMarketTape() : '';
                    if (/Risk-On|Constructive/i.test(tape)) { nudge += 2.5; ctxBits.push('tape risk-on'); }
                    else if (/Caution|Narrowing|Risk-Off|Defensive/i.test(tape)) { nudge -= 2.5; ctxBits.push('tape caution'); }
                    nudge = Math.max(-5, Math.min(5, Math.round(nudge)));

                    const raw = Math.max(0, Math.min(100, base + nudge));
                    const gatePass = Number.isFinite(rvol) && rvol >= CONFIRM.RVOL_GATE && stage2;
                    let grade;
                    if (gatePass && raw >= 78) grade = 'A';
                    else if (gatePass && raw >= 58) grade = 'B';
                    else if (raw >= 45) grade = 'C';
                    else grade = 'D';
                    const state = (typeof _deps.confirmationState === 'function') ? _deps.confirmationState() : 'final';
                    return {
                        score: raw, grade, gatePass, state, projRvol: rvol, rawRvol, rsiExp, rsiPts,
                        trigger: Math.round(trigger), setup: Math.round(setup), context: nudge, base, ctxBits, tape
                    };
                }
            };

            // Resolve an alert's confidence. Beta/Alpha now use the SERVER-computed rating
            // (two separate systems — lib/signals.js) carried on the row; other types fall
            // back to the legacy client CONFIRM.score. Note: beta/alpha bypass CONFIRM, so the
            // mis-signed risk-on context nudge no longer affects them (replaced by server
            // dipAligned / relStrInDip).
            function confFor(type, snap) {
                if ((type === 'beta' || type === 'alpha') && snap && snap[type]) {
                    const sr = snap[type], c = sr.components || {};
                    return {
                        score: sr.conf, grade: sr.grade, gatePass: sr.gatePass, state: sr.state,
                        confirmPct: Number.isFinite(sr.confirmPct) ? sr.confirmPct : null,
                        rawRvol: Number.isFinite(c.reclaimRvol) ? c.reclaimRvol : null,
                        components: sr.components, server: true
                    };
                }
                if (type === 'alpha' || type === 'beta' || type === 'oprime' || type === 'onova') return CONFIRM.score(type, snap);
                return { score: 60, grade: 'B', gatePass: !!snap.stage2 };
            }

            function pushAlert(item, type, snap, opts = {}) {
                const baseId = alertId(item.ticker, type, dayKeyFor(type, snap));
                const id = opts.forming ? `f-${baseId}` : baseId;
                if (item.alerts.some(a => a.id === id)) return false; // dedupe
                const conf = confFor(type, snap);
                item.alerts.push({
                    id,
                    type,                       // alpha | beta | omega | delta
                    status: opts.forming ? 'forming' : 'confirmed',
                    confScore: conf.score,
                    grade: conf.grade,
                    gatePass: conf.gatePass,
                    confTrigger: conf.trigger ?? null, confSetup: conf.setup ?? null,
                    confContext: conf.context ?? null, confBase: conf.base ?? null,
                    confCtxBits: conf.ctxBits || [], confTape: conf.tape || null,
                    confState: conf.state || 'final',
                    sigState: conf.server ? conf.state : null, sigConfirmPct: conf.server ? (conf.confirmPct ?? null) : null, confComponents: conf.components || null,
                    avwapDefense: Number.isFinite(snap.avwapDefense) ? snap.avwapDefense : null,
                    rsPowerTier: snap.rsPowerTier ?? null,
                    dollarVolume: Number.isFinite(snap.dollarVolume) ? snap.dollarVolume : null,
                    proximity: opts.forming ? (snap.forming ? snap.forming.proximity : null) : null,
                    price: snap.price ?? null,
                    detail: opts.forming ? formingDetail(snap) : alertDetail(type, snap, conf),
                    firedAt: Date.now(),
                    read: false
                });
                if (item.alerts.length > ALERT_CAP) item.alerts = item.alerts.slice(-ALERT_CAP);
                return true;
            }

            // Flat, newest-first view of the MARKET-WIDE log (watchlist no longer gates it).
            // Each entry is tagged `watched` so the UI can mark non-watchlisted names.
            function allAlerts() {
                const watchedSet = new Set(lists.flatMap(l => l.items.map(i => i.ticker)));
                return alertLog
                    .map(a => ({ ...a, watched: watchedSet.has(a.ticker) }))
                    .sort((x, y) => (y.firedAt || 0) - (x.firedAt || 0));
            }
            function unreadCount() {
                return alertLog.reduce((n, a) => n + (a.read ? 0 : 1), 0);
            }
            function markAllRead() {
                let mutated = false;
                alertLog.forEach(a => { if (!a.read) { a.read = true; mutated = true; } });
                if (mutated) persistLog();
            }
            function clearAlerts() {
                alertLog = [];
                persistLog();
            }

            // Update each watched item's snapshot from the freshest scored rows, AND
            // fire confirmed alerts on signal transitions. Returns {deltas, newAlerts}.
            // First sync of any ticker only establishes the baseline (no alerts fired),
            // so adding a hot stock never floods the feed.
            function sync(scoredRows) {
                if (!Array.isArray(scoredRows) || !scoredRows.length) return { deltas: [], newAlerts: 0 };
                const byTicker = new Map(scoredRows.map(r => [r.ticker, r]));
                const deltas = [];
                let mutated = false;
                let newAlerts = 0;
                items.forEach(item => {
                    const row = byTicker.get(item.ticker);
                    if (!row) return; // not in this scan — keep last snapshot, no alerts
                    if (!item.alerts) item.alerts = [];
                    const prev = item.snapshot || {};
                    const next = snapshotFromRow(row);
                    const hadBaseline = !!item._baselined;

                    // Detect notable changes (Phase B flags).
                    const changes = {};
                    if (prev.score != null && next.score != null && next.score !== prev.score)
                        changes.score = { from: prev.score, to: next.score };
                    if (prev.stage != null && next.stage != null && next.stage !== prev.stage)
                        changes.stage = { from: prev.stage, to: next.stage };
                    if (prev.signal !== next.signal)
                        changes.signal = { from: prev.signal, to: next.signal };

                    // Fire alerts only after a baseline exists (suppress first-ever sync).
                    if (hadBaseline) {
                        firedTransitions(prev, next).forEach(type => {
                            if (pushAlert(item, type, next)) newAlerts++;
                        });
                        // FORMING (pre-trigger) — fire when a stock newly enters a forming
                        // state with meaningful proximity, so you get the early edge before
                        // the actual cross. Deduped per type+day like confirmed alerts.
                        const nf = next.forming, pf = prev.forming;
                        if (nf && nf.proximity >= 60 && (!pf || pf.type !== nf.type)) {
                            if (pushAlert(item, nf.type, next, { forming: true })) newAlerts++;
                        }
                    }

                    item.snapshot = next;
                    item._baselined = true;
                    mutated = true;
                    if (Object.keys(changes).length) deltas.push({ ticker: item.ticker, changes });
                });
                if (mutated) persist();
                return { deltas, newAlerts };
            }

            // ── MARKET-WIDE ALERT LOG ─────────────────────────────────────────────────
            // Walk EVERY enriched scanned row (not just the watchlist) and append a log
            // entry whenever a signal transitions absent→present. Per-ticker last-seen
            // state lives in signalState so transitions are detected across scans without
            // bloating the watchlist. First time we ever see a ticker we only baseline it
            // (no alert) so the first scan doesn't dump the whole market into the log.
            function logId(ticker, type, snap, forming) {
                return `${forming ? 'f-' : ''}${ticker}-${type}-${dayKeyFor(type, snap)}`;
            }
            function pushLogAlert(ticker, name, type, snap, opts = {}) {
                const id = logId(ticker, type, snap, opts.forming);
                if (alertLog.some(a => a.id === id)) return false; // dedupe across history
                const conf = (type === 'alpha' || type === 'beta' || type === 'oprime' || type === 'onova')
                    ? CONFIRM.score(type, snap) : { score: 60, grade: 'B', gatePass: !!snap.stage2 };
                alertLog.push({
                    id, ticker, name: name || ticker, type,
                    status: opts.forming ? 'forming' : 'confirmed',
                    confScore: conf.score, grade: conf.grade, gatePass: conf.gatePass,
                    confTrigger: conf.trigger ?? null, confSetup: conf.setup ?? null,
                    confContext: conf.context ?? null, confBase: conf.base ?? null,
                    confCtxBits: conf.ctxBits || [], confTape: conf.tape || null,
                    confState: conf.state || 'final',
                    avwapDefense: Number.isFinite(snap.avwapDefense) ? snap.avwapDefense : null,
                    rsPowerTier: snap.rsPowerTier ?? null,
                    dollarVolume: Number.isFinite(snap.dollarVolume) ? snap.dollarVolume : null,
                    proximity: opts.forming ? (snap.forming ? snap.forming.proximity : null) : null,
                    price: snap.price ?? null,
                    detail: opts.forming ? formingDetail(snap) : alertDetail(type, snap, conf),
                    firedAt: Date.now(), read: false
                });
                if (alertLog.length > LOG_CAP) alertLog = alertLog.slice(-LOG_CAP);
                return true;
            }
            function logMarket(scoredRows) {
                if (!Array.isArray(scoredRows) || !scoredRows.length) return 0;
                let fired = 0;
                scoredRows.forEach(row => {
                    if (!row || !row.ticker) return;
                    const next = snapshotFromRow(row);
                    const prevState = signalState[row.ticker];
                    if (prevState) {
                        // Build a faux "prev" snapshot for transition detection from saved state.
                        const prev = {
                            hasAlpha: prevState.hasAlpha, hasBeta: prevState.hasBeta,
                            omegaSignal: prevState.omega || 'NONE', deltaTrigger: prevState.deltaTrigger,
                            forming: prevState.forming || null
                        };
                        firedTransitions(prev, next).forEach(type => {
                            if (pushLogAlert(row.ticker, row.name, type, next)) fired++;
                        });
                        const nf = next.forming, pf = prev.forming;
                        if (nf && nf.proximity >= 60 && (!pf || pf.type !== nf.type)) {
                            if (pushLogAlert(row.ticker, row.name, nf.type, next, { forming: true })) fired++;
                        }
                    }
                    // Update last-seen state (baseline on first sight → no alert this pass).
                    signalState[row.ticker] = {
                        hasAlpha: !!next.hasAlpha, hasBeta: !!next.hasBeta,
                        omega: next.omegaSignal || 'NONE', deltaTrigger: !!next.deltaTrigger,
                        forming: next.forming ? { type: next.forming.type } : null
                    };
                });
                if (fired || Object.keys(signalState).length) persistLog();
                return fired;
            }

            // STATE VIEW (not a transition log): every row that CURRENTLY holds an active
            // signal becomes an alert — independent of whether the client witnessed the
            // false→true flip. This is what the Alerts list renders, so a fresh signal
            // (e.g. a beta within BETA_RECENCY_BARS) can never be missing from the list
            // just because no transition was observed. Built from the SAME snapshot +
            // CONFIRM scoring pushLogAlert uses, so every rendered field is identical.
            function currentAlerts(scoredRows) {
                const rows = Array.isArray(scoredRows) ? scoredRows : [];
                const watchedSet = new Set(lists.flatMap(l => l.items.map(i => i.ticker)));
                const out = [];
                for (const row of rows) {
                    if (!row || !row.ticker) continue;
                    const snap = snapshotFromRow(row);
                    const types = [];
                    if (snap.hasAlpha) types.push('alpha');
                    if (snap.hasBeta) types.push('beta');
                    if (snap.deltaTrigger) types.push('delta');
                    const om = omegaTypeOf(snap); if (om) types.push(om);
                    for (const type of types) {
                        const conf = confFor(type, snap);
                        out.push({
                            id: `state-${row.ticker}-${type}`,
                            ticker: row.ticker, name: row.name || row.ticker, type, status: 'confirmed',
                            confScore: conf.score, grade: conf.grade, gatePass: conf.gatePass,
                            confTrigger: conf.trigger ?? null, confSetup: conf.setup ?? null,
                            confContext: conf.context ?? null, confBase: conf.base ?? null,
                            confCtxBits: conf.ctxBits || [], confTape: conf.tape || null,
                            confState: conf.state || 'final',
                            sigState: conf.server ? conf.state : null, sigConfirmPct: conf.server ? (conf.confirmPct ?? null) : null, confComponents: conf.components || null,
                            avwapDefense: Number.isFinite(snap.avwapDefense) ? snap.avwapDefense : null,
                            rsPowerTier: snap.rsPowerTier ?? null,
                            dollarVolume: Number.isFinite(snap.dollarVolume) ? snap.dollarVolume : null,
                            price: snap.price ?? null,
                            detail: alertDetail(type, snap, conf),
                            // For the "When" column: detection age (market days) + first-seen
                            // timestamp (signalState, alpha/beta only) for the forming case.
                            days: type === 'alpha' ? snap.alphaDays : type === 'beta' ? snap.betaDays : type === 'delta' ? snap.deltaDays : null,
                            firstSeenAt: (type === 'alpha' ? (snap.alpha && snap.alpha.firstSeenAt) : type === 'beta' ? (snap.beta && snap.beta.firstSeenAt) : null) || null,
                            firedAt: null, read: true, watched: watchedSet.has(row.ticker)
                        });
                    }
                }
                return out;
            }

            // ── Prefs ───────────────────────────────────────────────────────────────
            function getPrefs() { return prefs; }
            function setSort(key, dir) { prefs.sortKey = key; prefs.sortDir = dir; persistPrefs(); }

            hydrate();
            // Backfill: items added before alerts existed get an empty array + baseline flag
            // on next sync (so old watchlist members don't dump alerts retroactively).
            items.forEach(i => { if (!i.alerts) i.alerts = []; });

            return {
                has, hasAny, listsContaining, get, add, remove, toggle, addTo, removeFrom, update, addMany, removeMany, all, count, sync, logMarket,
                snapshotFromRow, getPrefs, setSort,
                allAlerts, currentAlerts, unreadCount, markAllRead, clearAlerts,
                // server-backed multi-list lifecycle
                load, isLoaded, reset, getLists, getActiveId, setActive, createList, renameList, deleteList, reorderLists,
                // sections + manual item ordering
                getSections, reorderItems, addSection, renameSection, deleteSection, reorderSections,
                _debug: () => ({ lists, activeListId, items, prefs, alertLog, signalState })
            };
        })();

if (typeof window !== 'undefined') {
    window.Watchlist = Watchlist;                     // preserve the singleton global (inline HTML / cross-module)
    window.watchlist = { configure, deps: () => _deps }; // module DI/verification surface
}
